<template>
  <div class="home h-100">
    <div class="row h-100">
      <Loader v-if="mapHidden" />
      <template v-else>
        <Regions
          v-if="$route.name === 'regions'"
          :handleTileClick="handleTileClick"
          :handleMapMobileToggle="handleMapMobileToggle"
          :mapHidden="mapHidden"
          :mapExpanded="mapExpanded"
          :mapMobile="mapMobile"
          :visibleMapRegions="visibleMapRegions"
        />

        <Observations
          v-else-if="$route.name === 'observations'"
          :handleTileClick="handleTileClick"
          :handleMapMobileToggle="handleMapMobileToggle"
          :mapHidden="mapHidden"
          :mapExpanded="mapExpanded"
          :mapMobile="mapMobile"
          :onObservationSelect="handleObservationSelect"
        />
      </template>

      <Mapbox
        :styles="{ 'map-wrapper--absolute': true, 'map-wrapper--mobile': mapMobile }"
        :hidden="mapHidden"
        :expandable="true"
        :expanded="mapExpanded"
        :regions="regions"
        :forecasts="forecasts"
        :observations="observations"
        :selectedRegion="selectedRegion.id"
        :onLoad="handleMapLoad"
        :onResize="handleMapResize"
        :onMobileToggle="handleMapMobileToggle"
        :onSizeToggle="handleMapSizeToggle"
        :onRegionSelect="handleRegionSelect"
        :onObservationSelect="handleObservationSelect"
        :onDragEnd="handleDragEnd"
        :onZoomEnd="handleMapZoomEnd"
        :onTileClick="handleTileClick"
        :showSizeToggle="$route.name !== 'observations'"
      >
        <MapFilter v-if="$route.name !== 'observations'" slot="map-filter" />
      </Mapbox>
    </div>
  </div>
</template>

<script>
import Loader from "../components/loader/Loader";
import Mapbox from "../components/map/Map";
import MapFilter from "../components/map/MapFilter";
import Regions from "../components/panels/Regions";
import Observations from "../components/panels/Observations";
import { mapState } from "vuex";
import Vue from "vue";

export default {
  components: {
    Loader,
    Mapbox,
    MapFilter,
    Regions,
    Observations
  },

  data() {
    return {
      mapHidden: true,
      mapExpanded: false,
      mapMobile: false,
      visibleMapRegions: []
    };
  },

  computed: {
    ...mapState({
      cms: state => state.cms,
      regions: state => state.regions.filter(region => region.geometry !== null),
      selectedRegion: state => state.selectedRegion,
      forecasts: state => state.forecasts,
      showRecentPublicObs: state => state.showRecentPublicObs
    }),

    observations() {
      if (this.$route.name === "regions") {
        return this.showRecentPublicObs
          ? this.$store.getters.recentObservationsGeoJson()
          : this.$store.getters.allObservationsGeoJson();
      } else if (this.$route.name === "observations") {
        return this.$store.getters.filteredObservationsGeoJson();
      }
    }
  },

  methods: {
    handleFilterButtonClick(event, filterOn) {
      const filterChanged = this.currentFilter !== filterOn;

      this.currentFilter = filterOn;

      // Only update the order when toggling the current filter,
      // never when switching between filters.
      if (!filterChanged) {
        this.currentFilterOrder[filterOn] = this.currentFilterOrder[filterOn] === "ASC" ? "DESC" : "ASC";
      }
    },

    handleMapLoad(map) {
      this.mapHidden = false;

      // Because the map is initially hidden, we have to resize after it's shown.
      // See https://www.mapbox.com/mapbox-gl-js/api/#map#resize
      Vue.nextTick(() => {
        map.resize();

        //Show all regions when the Mapload. Those code are design for the IE
        var regionIds = [];
        this.regions.forEach(region => {
          regionIds.push(region.id);
        });
        this.visibleMapRegions = regionIds;
      });
    },

    handleMapMobileToggle(event, map) {
      this.mapMobile = !this.mapMobile;

      Vue.nextTick(() => {
        if (typeof map !== "undefined") {
          map.resize();
        } else {
          window.dispatchEvent(new Event("resize"));
        }
      });
    },

    handleMapSizeToggle(event, map) {
      this.mapExpanded = !this.mapExpanded;

      Vue.nextTick(() => {
        map.resize();
      });
    },

    handleMapResize(map, visibleRegionIds) {
      this.visibleMapRegions = visibleRegionIds;
    },

    handleDragEnd(map, visibleRegionIds) {
      this.visibleMapRegions = visibleRegionIds;
    },

    handleMapZoomEnd(map, visibleRegionIds) {
      this.visibleMapRegions = visibleRegionIds;
    },

    handleTileClick(event, id) {
      event.preventDefault();
      this.$store.dispatch("setSelectedRegion", this.$store.getters.regionById(id));
      this.$router.push({ name: "region", params: { id } });
    },

    handleRegionSelect(region) {
      this.$store.dispatch("setSelectedRegion", region);
    },

    handleObservationSelect(id) {
      this.$store.dispatch("setSelectedObservation", this.$store.getters.observationById(id));
      this.$router.push({ name: "observation", params: { id } });
    }
  },

  // Meta tags are automatically inserted by vue-head library.
  head: {
    meta: function() {
      let tags = [];
      if (this.cms.socialTitle) {
        tags.push({ property: "og:title", content: this.cms.socialTitle });
      }

      if (this.cms.socialDescription && this.cms.socialDescription.length > 0) {
        tags.push({ property: "og:description", content: this.cms.socialDescription });
      }

      if (this.cms.socialImage && this.cms.socialImage.length > 0) {
        tags.push({ property: "og:image", content: this.cms.socialImage });
        tags.push({ property: "og:image:width", content: 300 });
        tags.push({ property: "og:image:height", content: 300 });
      }

      return tags;
    }
  },

  created() {
    this.$store.dispatch("clearSelectedRegion");
  }
};
</script>
